import { Box, styled } from "@mui/material";

import {
  Bubble,
  Composer,
  ComposerField,
  ComposerPlus,
  ImageBubble,
  IOS,
  IOS_FONT,
  NavAvatar,
  NavBack,
  NavBar,
  NavIdentity,
  NavName,
  NavVideo,
  Pill,
  PillRow,
  RcsCta,
  Stamp,
  Thread,
  ThreadStack,
} from "./ios.styles";
import {
  ChevronLeftGlyph,
  ChevronRightGlyph,
  MicGlyph,
  PersonGlyph,
  PlusGlyph,
  VideoGlyph,
} from "./iosGlyphs";
import { ContactCardPreview } from "../ContactCardPreview";
import type { PhoneMessage } from "../phoneMessage";
import { renderMessageWithUrls } from "../previewContentUtils";
import { PreviewErrorBubble } from "../PreviewErrorBubble";

import {
  LinkIcon,
  PhoneIcon,
  ShoppingCartIcon,
  SmsOutlinedIcon,
} from "@/icons";

const SUGGESTION_PILL_ICONS = {
  REPLY: SmsOutlinedIcon,
  OPEN_URL: LinkIcon,
  DIAL_PHONE: PhoneIcon,
  PRODUCT: ShoppingCartIcon,
} as const;

function MessageBody({ message }: { message: PhoneMessage }) {
  const showTextBubble =
    message.text.length > 0 || message.ctaText !== null || !message.imageUrl;

  return (
    <>
      {message.imageUrl ? (
        <ImageBubble>
          <img src={message.imageUrl} alt={message.imageAlt} />
        </ImageBubble>
      ) : null}

      {showTextBubble ? (
        <Bubble data-testid="ios-bubble" hint={message.showEmptyHint}>
          {renderMessageWithUrls(message.text, true)}
          {message.ctaText ? (
            <RcsCta divided={message.text.length > 0}>{message.ctaText}</RcsCta>
          ) : null}
        </Bubble>
      ) : null}

      {message.documentName ? (
        <ContactCardPreview name={message.documentName} />
      ) : null}

      {message.suggestions.length > 0 ? (
        <PillRow>
          {message.suggestions.map((suggestion, index) => {
            const Glyph = SUGGESTION_PILL_ICONS[suggestion.type];
            return (
              <Pill key={index}>
                <Glyph />
                {suggestion.text}
              </Pill>
            );
          })}
        </PillRow>
      ) : null}
    </>
  );
}

interface IosMessagesScreenProps {
  message: PhoneMessage;
  onRetry?: () => void;
}

function ThreadBody({ message, onRetry }: IosMessagesScreenProps) {
  return message.isError ? (
    <PreviewErrorBubble
      onRetry={onRetry}
      maxWidth={270}
      borderRadius={18}
      fontSize={17}
      lineHeight={22}
    />
  ) : (
    <MessageBody message={message} />
  );
}

const BubbleStack = styled(Box)({
  fontFamily: IOS_FONT,
  color: IOS.ink,
  WebkitFontSmoothing: "antialiased",
  display: "flex",
  flexDirection: "column",
  alignItems: "flex-start",
  gap: 8,
  "& *": { boxSizing: "border-box" },
});

export function IosBubbles({ message, onRetry }: IosMessagesScreenProps) {
  return (
    <BubbleStack>
      <ThreadBody message={message} onRetry={onRetry} />
    </BubbleStack>
  );
}

export function IosMessagesScreen({
  message,
  onRetry,
}: IosMessagesScreenProps) {
  return (
    <>
      <NavBar>
        <NavBack>
          <ChevronLeftGlyph />
        </NavBack>
        <NavIdentity>
          <NavAvatar>
            <PersonGlyph size={30} />
          </NavAvatar>
          <NavName>
            <span>{message.sender}</span>
            <ChevronRightGlyph />
          </NavName>
        </NavIdentity>
        <NavVideo>
          <VideoGlyph />
        </NavVideo>
      </NavBar>

      <Thread>
        <ThreadStack>
          <Stamp>
            <b>Today</b> 9:41 AM
          </Stamp>
          <ThreadBody message={message} onRetry={onRetry} />
        </ThreadStack>
      </Thread>

      <Composer>
        <ComposerPlus>
          <PlusGlyph />
        </ComposerPlus>
        <ComposerField>
          <span>
            {message.channel === "RCS"
              ? "Text Message · RCS"
              : "Text Message · SMS"}
          </span>
          <MicGlyph />
        </ComposerField>
      </Composer>
    </>
  );
}
